<template>
    <div class="c-recaptcha-wrapper">
        <div ref="container" />
        <c-error v-if="error" :message="errorMsg" />
    </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';
import CError from '@/components/shared/CError.vue';

const props = withDefaults(
    defineProps<{
        value?: string;
        siteKey?: string;
        error?: boolean;
        errorMsg?: string;
    }>(),
    {
        siteKey: process.env.VUE_APP_RECAPTCHA_SITE_KEY
    }
);

const emit = defineEmits<{
    (event: 'input', token: string): void;
}>();

const container = ref<HTMLElement | undefined>();
const widgetId = ref<number | undefined>();

// api.js?render=explicit is loaded from public/index.html, but it may not have resolved yet
const waitForGrecaptcha = () =>
    new Promise<void>((resolve) => {
        if (window.grecaptcha?.render) return resolve();
        const interval = setInterval(() => {
            if (!window.grecaptcha?.render) return;
            clearInterval(interval);
            resolve();
        }, 100);
    });

const reset = () => {
    if (widgetId.value == undefined) return;
    window.grecaptcha?.reset(widgetId.value);
    emit('input', '');
};

onMounted(async () => {
    await waitForGrecaptcha();
    if (!container.value || !window.grecaptcha) return;

    widgetId.value = window.grecaptcha.render(container.value, {
        sitekey: props.siteKey || '',
        callback: (token: string) => emit('input', token),
        'expired-callback': () => emit('input', ''),
        'error-callback': () => emit('input', '')
    });
});

onBeforeUnmount(reset);

defineExpose({ reset });
</script>

<style lang="scss" scoped>
.c-recaptcha-wrapper {
    :deep(> div > div) {
        margin: auto;
    }
}
</style>
